import React, { useContext, useEffect, useMemo } from 'react';
import iro from '@jaames/iro/';
import { ColorPicker } from 'react-iro';

import { Box } from '@mui/system';
import { LeafStateContext } from 'context/state/leaf';
import { useAspenSocket } from 'hooks/useAspenSocket';
import { getColorProperties } from 'modules/color';
import { LightState } from 'types/aspen/state';

import styles from '../colorPicker.module.scss';

interface RgbControlProps {
  initialRender: boolean;
}

export const RgbControl = ({ initialRender }: RgbControlProps) => {
  const { leaf, dispatch } = useContext(LeafStateContext);
  const leafState = leaf.state as LightState;
  const brightnessLevel = leafState.brightness?.level;
  const { sendCommand } = useAspenSocket();
  const { rgbColor } = getColorProperties(leafState, brightnessLevel);
  const rgbColorIro = useMemo(() => new iro.Color(rgbColor), [rgbColor]);

  // Create a bright version of the color for the picker display (always at 100% brightness)
  const brightColorForPicker = useMemo(() => {
    const color = new iro.Color(rgbColor);
    const hsvColor = { ...color.hsv, v: 100 };
    return new iro.Color(hsvColor).rgbString;
  }, [rgbColor]);

  useEffect(() => {
    if (!initialRender) {
      handleColorChange(rgbColorIro);
    }
  }, []);

  const handleColorChange = (color: iro.Color) => {
    // change to hsv color , for a brighter color , accurate representation
    // set the value of the color at 100% brightness
    const hsvColor = { ...color.hsv, v: 100 };
    const hsvIro = new iro.Color(hsvColor);
    const rgbArray = Object.values(hsvIro.rgb) as [number, number, number];

    const colorPayload = {
      colorType: 'rgb' as const,
      rgb: rgbArray,
    };

    dispatch({ type: 'SET_COLOR', payload: colorPayload, origin: { type: leaf.type, trigger: 'user' } }, () =>
      sendCommand({
        leaf,
        command: { command: 'color', parameters: colorPayload },
      }),
    );
  };

  return (
    <Box className={styles.colorPickerContainer}>
      <ColorPicker
        setters={{
          onChangeColor: handleColorChange,
        }}
        options={{
          color: brightColorForPicker,
          width: 300,
          height: 300,
          handleRadius: 20,
          activeHandleRadius: 25,
          wheelLightness: true,
          layout: [
            {
              component: iro.ui.Wheel,
            },
          ],
        }}
      />
    </Box>
  );
};
